import { useTranslation } from "react-i18next";
import type { VideoTrack } from "twilio-video";
import { cn } from "@tellescope/ui";
import { useTrackAttachment } from "../hooks/useTrackAttachment";

export interface ScreenShareStageProps {
  /** The screen video track to display (local or remote). */
  track: VideoTrack;
  /** Display name of the remote person sharing; ignored when isLocal is true. */
  sharerName?: string;
  /** Whether this is the local user's own screen. */
  isLocal?: boolean;
  /** Additional class name. */
  className?: string;
}

/**
 * ScreenShareStage - Displays a shared screen as the main, prominent feed.
 *
 * Uses object-contain on a black background so the full screen is visible without cropping.
 * Audio for the shared screen is intentionally not handled here — it plays through the
 * sharer's participant tile (see VideoParticipant) so it stays audible regardless of layout.
 */
export const ScreenShareStage = ({ track, sharerName, isLocal = false, className }: ScreenShareStageProps) => {
  const { t } = useTranslation("videoCalling");
  const videoRef = useTrackAttachment<HTMLVideoElement>(track);

  const label = isLocal
    ? t("You are presenting")
    : sharerName
    ? t("{{name}} is presenting", { name: sharerName })
    : t("Screen share");

  return (
    <div className={cn("relative w-full h-full bg-black rounded-lg overflow-hidden", className)}>
      <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-contain" />
      <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-white text-sm">{label}</div>
    </div>
  );
};
